import CodeIcon from "@mui/icons-material/Code";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import StarIcon from "@mui/icons-material/Star";
import StorageIcon from "@mui/icons-material/Storage";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";
import GlassCard from "./GlassCard";

const USERNAME = "patriciam97";

function StatCard({ icon, label, value, color = "#FF83A8" }) {
  return (
    <GlassCard
      sx={{
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        height: "100%",
        transition: "border 0.25s, box-shadow 0.25s",
        "&:hover": {
          border: "1px solid rgba(255,131,168,0.6)",
          boxShadow: "0 8px 36px rgba(255,131,168,0.08)",
        },
      }}
    >
      <Box sx={{ color, opacity: 0.85 }}>{icon}</Box>
      <Typography
        variant="h4"
        sx={{
          fontFamily: "Roboto",
          fontWeight: 700,
          color: "#fff",
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          fontFamily: "RobotoLight",
          fontWeight: 300,
          color: "rgba(255,255,255,0.55)",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          fontSize: "0.65rem",
          textAlign: "center",
        }}
      >
        {label}
      </Typography>
    </GlassCard>
  );
}

function LangBar({ lang, pct, color }) {
  return (
    <Tooltip title={`${lang} — ${pct}%`} placement="top" arrow>
      <Box
        sx={{
          flex: `0 0 ${pct}%`,
          height: "100%",
          backgroundColor: color,
          "&:first-of-type": { borderRadius: "6px 0 0 6px" },
          "&:last-of-type": { borderRadius: "0 6px 6px 0" },
          cursor: "default",
        }}
      />
    </Tooltip>
  );
}

const LANG_COLORS = {
  JavaScript: "#f1e05a",
  TypeScript: "#3178c6",
  Python: "#3572A5",
  Java: "#b07219",
  HTML: "#e34c26",
  CSS: "#563d7c",
  "C#": "#178600",
  "C++": "#f34b7d",
  Ruby: "#701516",
  Go: "#00ADD8",
  Kotlin: "#A97BFF",
  Swift: "#F05138",
  Rust: "#dea584",
  Shell: "#89e051",
  Vue: "#41b883",
  Scala: "#c22d40",
  Other: "rgba(255,255,255,0.18)",
};

function langColor(name) {
  return LANG_COLORS[name] ?? LANG_COLORS.Other;
}

export default function GitHubStats() {
  const isMobile = useMediaQuery("(max-width: 700px)");
  const [stats, setStats] = useState(null);
  const [langs, setLangs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch("/.netlify/functions/github-stats");
        if (!res.ok) throw new Error("Function error");
        const data = await res.json();
        if (cancelled) return;
        setStats(data);
        setLangs(
          (data.langs ?? []).map((l) => ({ ...l, color: langColor(l.lang) })),
        );
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Section header */}
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 24,
              background: "#FF83A8",
              borderRadius: "2px",
              flexShrink: 0,
            }}
          />
          <Typography
            component="h5"
            variant="h5"
            color="secondary"
            sx={{ fontFamily: "Roboto", fontWeight: 700 }}
          >
            GitHub
          </Typography>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress color="secondary" size={32} />
        </Box>
      )}

      {error && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.4)", fontFamily: "RobotoLight" }}
          >
            Could not load GitHub data.
          </Typography>
        </Box>
      )}

      {!loading && !error && stats && (
        <>
          {/* Stat cards */}
          <Box>
            <Grid container spacing={2} sx={{ justifyContent: "center" }}>
              {[
                {
                  icon: <StorageIcon />,
                  label: "Public Repos",
                  value: stats.publicRepos,
                },
                {
                  icon: <GroupIcon />,
                  label: "Followers",
                  value: stats.followers,
                },
                {
                  icon: <PersonAddIcon />,
                  label: "Following",
                  value: stats.following,
                },
                {
                  icon: <StarIcon />,
                  label: "Stars Earned",
                  value: stats.stars,
                },
                ...(stats.privateCount !== null
                  ? [
                      {
                        icon: <LockIcon />,
                        label: "Private Repos",
                        value: stats.privateCount,
                      },
                    ]
                  : []),
              ].map((s) => (
                <Grid
                  size={{ xs: 6, sm: 4, md: 2 }}
                  key={s.label}
                  sx={{ display: "flex" }}
                >
                  <StatCard {...s} />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Language bar */}
          {langs.length > 0 && (
            <Box
              sx={{
                mx: "auto",
                width: "100%",
                maxWidth: { xs: "100%", sm: "83.33%", md: "66.67%" },
              }}
            >
              <GlassCard sx={{ padding: "20px 24px" }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.6)",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    mb: 1.5,
                  }}
                >
                  Most used languages
                </Typography>

                {/* Bar */}
                <Box
                  sx={{
                    display: "flex",
                    height: "10px",
                    width: "100%",
                    borderRadius: "6px",
                    overflow: "hidden",
                    gap: "2px",
                    mb: 1.5,
                  }}
                >
                  {langs.map((l) => (
                    <LangBar key={l.lang} {...l} />
                  ))}
                </Box>

                {/* Legend */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: isMobile ? 1 : 1.5,
                  }}
                >
                  {langs.map((l) => (
                    <Box
                      key={l.lang}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: l.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: "RobotoLight",
                          color: "rgba(255,255,255,0.65)",
                          fontSize: "0.72rem",
                        }}
                      >
                        {l.lang}{" "}
                        <Box
                          component="span"
                          sx={{ color: "rgba(255,255,255,0.35)" }}
                        >
                          {l.pct}%
                        </Box>
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </GlassCard>
            </Box>
          )}

          {/* Profile link */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              component="a"
              href={`https://github.com/${USERNAME}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                textDecoration: "none",
              }}
            >
              <CodeIcon sx={{ color: "rgba(255,255,255,0.3)", fontSize: 16 }} />
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "RobotoLight",
                  color: "rgba(255,255,255,0.3)",
                  fontSize: "0.72rem",
                  transition: "color 0.2s",
                  "&:hover": { color: "#FF83A8" },
                }}
              >
                github.com/{USERNAME}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
